<template>
    <v-container fluid>
        <ConfirmDialog
            :is-open="deleteAttachmentDialog"
            :data="currentAttachment"
            :action-loading="deleteAttachmentLoading"
            removal
            @on-confirm="deleteDocumentAttachment(currentAttachment)"
            @on-abort="closeConfirmDeleteAttachmentDialog"
        >
            <template #title>
                Usuwanie załącznika
            </template>
            <template #content>
                Czy na pewno chcesz usunąć załącznik: <strong>{{ getFileName(currentAttachment) }}</strong>?
            </template>
            <template #confirmButton>
                Usuń
            </template>
        </ConfirmDialog>

        <v-overlay
            color="white"
            opacity="0.85"
            absolute
            :value="fileProgress.show"
        >
            <v-container class="pa-0">
                <v-row justify="center">
                    <v-col cols="auto">
                        <v-progress-circular
                            :rotate="-90"
                            :size="120"
                            :width="4"
                            :value="fileProgress.value"
                            color="primary"
                        >
                            {{ fileProgress.value }}
                        </v-progress-circular>
                    </v-col>
                </v-row>
                <v-row justify="center">
                    <span class="text-caption primary--text">{{ fileProgress.fileName }}</span>
                </v-row>
                <v-row justify="center">
                    <v-col cols="auto">
                        <v-btn
                            color="error"
                            small
                            outlined
                            data-sentry-label="Anuluj"
                            @click="abortUploadAttachments(fileProgress.fileName)"
                        >
                            Anuluj
                        </v-btn>
                    </v-col>
                </v-row>
            </v-container>
        </v-overlay>
        <v-row>
            <v-col cols="auto" sm="8" md="6" lg="8">
                <v-file-input
                    v-model="currentFiles"
                    label="Pliki"
                    multiple
                    counter
                    placeholder="Wybierz pliki"
                    prepend-icon="mdi-paperclip"
                    outlined
                    dense
                    :show-size="1000"
                    :disabled="isTabReadOnly"
                    :error-messages="currentDocumentAttachmentsErrors"
                >
                    <template #selection="{text}">
                        <v-chip label small>
                            {{ text }}
                        </v-chip>
                    </template>
                </v-file-input>
            </v-col>
            <v-col cols="auto">
                <v-btn
                    color="primary"
                    outlined
                    :disabled="isTabReadOnly || currentFiles.length === 0"
                    data-sentry-label="Dodaj"
                    @click="uploadAttachments"
                >
                    <v-icon left>
                        mdi-file-upload
                    </v-icon>
                    Dodaj
                </v-btn>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="auto" sm="12" md="6" lg="8">
                <v-data-table
                    v-if="Array.isArray(document.attachments) && document.attachments.length > 0"
                    :headers="headers"
                    :items="document.attachments"
                    :mobile-breakpoint="0"
                    disable-pagination
                    hide-default-footer
                >
                    <template #item.fileName="{item}">
                        <v-tooltip top :open-delay="tooltipDelay" :close-delay="tooltipDelay">
                            <template #activator="{on,attrs}">
                                <span
                                    v-bind="attrs"
                                    class="pointer"
                                    v-on="on"
                                    @click="openInNewTab(item)"
                                >{{ getDisplayName(item) }}</span>
                            </template>
                            <span>Kliknij żeby pobrać</span>
                        </v-tooltip>
                    </template>
                    <template #item.actions="{item}">
                        <v-tooltip top :open-delay="tooltipDelay" :close-delay="tooltipDelay">
                            <template #activator="{on,attrs}">
                                <span v-bind="attrs" v-on="on">
                                    <v-btn
                                        icon
                                        :disabled="isTabReadOnly"
                                        @click="showDeleteAttachmentDialog(item)"
                                    >
                                        <v-icon color="error">mdi-delete</v-icon>
                                    </v-btn>
                                </span>
                            </template>
                            <span>Usuń</span>
                        </v-tooltip>
                    </template>
                </v-data-table>
                <v-alert
                    v-else
                    type="info"
                    text
                    border="left"
                    class="mt-4"
                >
                    Nie dodano załączników.
                </v-alert>

                <v-alert
                    border="left"
                    color="blue lighten-4"
                    colored-border
                    icon="mdi-information-outline"
                    elevation="0"
                    class="mt-4"
                >
                    Dopuszczalny rozmiar pojedynczego pliku: <strong>{{ maxFileSizeMB }} MB</strong>
                </v-alert>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import { appConfig, apiConfig } from '@/config'
import tooltipDelay from '@/mixins/tooltip'
import parse from '@/mixins/parse'
import { errorHandler } from '@/utils/errorHandler'
import ConfirmDialog from '@/components/common/ConfirmDialog'
import { normalizeAttachment } from '@/views/Documents/Document/helpers/attachments'

const maxFileSize = (value) => {
    return value.size < appConfig.documents.documentAttachmentSize * 1024 * 1024
}

export default {
    name: 'AttachmentsTab',
    components: {
        ConfirmDialog
    },
    mixins: [
        tooltipDelay, parse
    ],
    props: {
        document: {
            type: Object,
            default: null
        },
        uploadedFiles: {
            type: Array,
            default: () => []
        },
        isReadOnly: {
            type: Boolean,
        }
    },
    data() {
        return {
            currentFiles: [],
            headers: [
                {
                    text: 'Nazwa pliku',
                    value: 'fileName',
                    align: 'left',
                    sortable: false,
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    align: 'center',
                    sortable: false,
                    width: 100
                },
            ],
            deleteAttachmentDialog: false,
            deleteAttachmentLoading: false,
            currentAttachment: {},
        }
    },
    validations() {
        const validations = {}

        if (this.currentFiles.length > 0) {
            validations['currentFiles'] = {
                $each: {
                    maxFileSize
                }
            }
        }

        return validations
    },
    computed: {
        isTabReadOnly() {
            return this.isReadOnly
        },
        fileProgress() {
            return this.$store.state.common.progress
        },
        userId() {
            return this.$store.state.user.userId
        },
        currentDocumentAttachmentsErrors() {
            if (this.currentFiles.length === 0) return

            const errors = []

            if (!this.$v.currentFiles?.$dirty) return errors
            this.$v.currentFiles.$invalid && errors.push(`Przekroczono dopuszczalny rozmiar pliku: ${appConfig.documents.documentAttachmentSize}MB.`)

            return errors
        },
        maxFileSizeMB() {
            return appConfig.documents.documentAttachmentSize
        }
    },
    methods: {
        getFileName(attachment) {
            if (attachment.name !== null && attachment.name !== undefined) {
                return attachment.name
            }

            return ''
        },
        uploadAttachments() {
            this.$v.currentFiles?.$touch()

            if (this.$v.currentFiles?.$invalid) return

            // If we're editing an existing document, upload immediately
            if (this.document.id) {
                const formData = new FormData()
                const file = this.currentFiles[0]

                formData.append('file', file)

                this.$store.dispatch('documents/createAttachment', {
                    formData: formData,
                    fileName: file.name,
                    docOrderId: this.document.id
                })
                    .then(response => {
                        const responseData = response?.data || {}
                        const normalized = normalizeAttachment({
                            ...responseData,
                            name: responseData.name || file.name
                        })

                        if (normalized) {
                            this.document.attachments.push(normalized)
                        }
                        this.currentFiles.shift()
                        // If there are more files to upload, continue
                        if (this.currentFiles.length > 0) {
                            this.uploadAttachments()
                        }
                    })
                    .catch(error => {
                        if (error.message !== 'canceled') {
                            errorHandler(error)
                        }
                    })
            } else {
                this.currentFiles.forEach(file => {
                    const temporary = normalizeAttachment({
                        id: 'temp-' + Date.now() + '-' + Math.random().toString(36).substring(2, 11),
                        name: file.name,
                        file: file,
                        isNew: true
                    })

                    if (temporary) {
                        this.document.attachments.push(temporary)
                    }
                })
                this.currentFiles = []
            }
        },
        abortUploadAttachments(fileName) {

            this.$store.dispatch('documents/createAttachment', {
                formData: null,
                fileName: fileName
            })
                .catch(error => {
                    if (error.message !== 'canceled') {
                        errorHandler(error)
                    }
                })
        },
        deleteDocumentAttachment(attachment) {
            this.deleteAttachmentLoading = true

            this.$store.dispatch('documents/deleteAttachment', {
                file: attachment
            })
                .then(() => {
                    const idx = this.document.attachments.findIndex(a => a.name === attachment.name)

                    if (idx > -1) {
                        this.document.attachments.splice(idx, 1)
                    }
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.closeConfirmDeleteAttachmentDialog()
                })
        },
        showDeleteAttachmentDialog(attachment) {
            if (this.isTabReadOnly) return

            this.currentAttachment = attachment
            this.deleteAttachmentDialog = true
        },
        closeConfirmDeleteAttachmentDialog() {
            this.deleteAttachmentDialog = false
            this.deleteAttachmentLoading = false
            this.currentAttachment = {}
        },
        openInNewTab(file) {
            const mediaRoot = apiConfig.FACTORYBASE_API_URI

            window.open(`${mediaRoot}${file.file}`, '_blank').focus()
        },
        getDisplayName(attachment) {
            // If attachment is an object with a name property, use that
            if (attachment && typeof attachment === 'object' && attachment.name) {
                return attachment.name
            }
            // If it's a URL string, extract the filename
            if (typeof attachment === 'string' && attachment.includes('://')) {
                return attachment.split('/').pop()
            }

            return attachment
        }
    }
}
</script>
